import {
  ModelRecord,
  IVisitClient,
  VisitClientModel,
  IVisitPet,
  VisitPetModel,
  IVisitIntakeCompletionStatus,
  VisitIntakeCompletionStatusModel,
  IReservationModel,
  ReservationModel,
  IVisitTag,
  VisitTagModel,
  VisitModel,
} from '@common/models';
import { VisitStatus, IntakeCompletionStatuses } from '@common/models/Enums';
import { SourceSystem } from '@common/models/Enums/SourceSystem';
import { DateTime } from 'luxon';
import { action, makeObservable, observable } from 'mobx';
import { ISyncModel, SyncModel } from '@common/models/SyncModel';

/**
 * FROM: VisitListingModel.cs
 */
export interface IVisitListing extends ISyncModel {
  clinicId: number;
  visitId: number;
  petId: number;
  clientId: number;
  isExpress: boolean;
  isRxOnly: boolean;
  visitStatusId: VisitStatus;
  visitStatusName: string;
  visitCompletionTypeId: number;
  visitCompletionTypeName: string;
  visitCompletionTypeIsComplete: boolean;
  visitCompletionTypeIsManuallyAssignable: boolean;
  checkinTime?: string | undefined;
  consultationStart?: string | undefined;
  consultation2Start?: string | undefined;
  vetServicesStart?: string | undefined;
  checkoutTime?: string | undefined;
  visitCheckinStart?: string | undefined;
  visitConsultationStart?: string | undefined;
  visitVetServicesStart?: string | undefined;
  visitCheckoutStart?: string | undefined;
  visitInProgressStart?: string | undefined;
  visitAdmittedStart?: string | undefined;
  visitFinalizedStart?: string | undefined;
  visitPaidStart?: string | undefined;
  smsStatus: number;
  hasVetAttestation: boolean;
  paymentMethodId?: number | undefined;
  cashPayment?: number | undefined;
  processedPhotoBaseUrl: string;
  photoUrl: string;
  serviceAmount: number;
  productAmount: number;
  licenseFeeAmount: number;
  vetcoPrescriptionAmount: number;
  discountAmount: number;
  taxPercentage: number;
  subtotalPreDiscountsAmount: number;
  subtotalPostDiscountsAmount: number;
  taxAmount: number;
  totalAmount: number;
  servicesRebateAmount?: number | undefined;
  productsRebateAmount?: number | undefined;
  totalRebateAmount?: number | undefined;
  nonTaxableFeeAmount?: number | undefined;
  taxableFeeAmount?: number | undefined;
  totalFeeAmount?: number | undefined;
  totalAOVAmount?: number | undefined;
  visitTagId?: number | undefined;
  reservationId?: number | undefined;
  approvedForVaccinations?: boolean | undefined;
  pet: IVisitPet;
  client: IVisitClient;
  paymentMethod: any; // PaymentMethodModel;
  visitTag: IVisitTag; // VisitTagModel;
  reservation: IReservationModel | undefined;
  arrived?: string | undefined;
  firstCheckIn?: string | undefined;
  visitStarted?: string | undefined;
  arrivedWaitingTime?: number | undefined;
  checkInWaitingTime?: number | undefined;
  intakeId?: number | undefined;
  intakePetId?: number | undefined;
  intakePublicId?: string | undefined;
  intakeCompletionStatusId?: IntakeCompletionStatuses | undefined;
  intakeCompletionStatus: IVisitIntakeCompletionStatus;
}

export class VisitListingModel
    extends SyncModel
  implements ModelRecord<IVisitListing>
{
  @observable clinicId: number;
  @observable visitId: number;
  @observable petId: number;
  @observable clientId: number;
  @observable isExpress: boolean;
  @observable isRxOnly: boolean;
  @observable visitStatusId: VisitStatus;
  @observable visitStatusName: string;
  @observable visitCompletionTypeId: number;
  @observable visitCompletionTypeName: string;
  @observable visitCompletionTypeIsComplete: boolean;
  @observable visitCompletionTypeIsManuallyAssignable: boolean;
  @observable checkinTime: DateTime | undefined;
  @observable consultationStart: DateTime | undefined;
  @observable consultation2Start: DateTime | undefined;
  @observable vetServicesStart: DateTime | undefined;
  @observable checkoutTime: DateTime | undefined;
  @observable visitCheckinStart: DateTime | undefined;
  @observable visitConsultationStart: DateTime | undefined;
  @observable visitVetServicesStart: DateTime | undefined;
  @observable visitCheckoutStart: DateTime | undefined;
  @observable visitInProgressStart: DateTime | undefined;
  @observable visitAdmittedStart: DateTime | undefined;
  @observable visitFinalizedStart: DateTime | undefined;
  @observable visitPaidStart: DateTime | undefined;
  @observable smsStatus: number;
  @observable hasVetAttestation: boolean;
  @observable paymentMethodId: number | undefined;
  @observable cashPayment: number | undefined;
  @observable processedPhotoBaseUrl: string;
  @observable photoUrl: string;
  @observable serviceAmount: number;
  @observable productAmount: number;
  @observable licenseFeeAmount: number;
  @observable vetcoPrescriptionAmount: number;
  @observable discountAmount: number;
  @observable taxPercentage: number;
  @observable subtotalPreDiscountsAmount: number;
  @observable subtotalPostDiscountsAmount: number;
  @observable taxAmount: number;
  @observable totalAmount: number;
  @observable servicesRebateAmount: number | undefined;
  @observable productsRebateAmount: number | undefined;
  @observable totalRebateAmount: number | undefined;
  @observable nonTaxableFeeAmount: number | undefined;
  @observable taxableFeeAmount: number | undefined;
  @observable totalFeeAmount: number | undefined;
  @observable totalAOVAmount: number | undefined;
  @observable visitTagId: number | undefined;
  @observable reservationId: number | undefined;
  @observable approvedForVaccinations: boolean | undefined;
  @observable pet: IVisitPet;
  @observable client: IVisitClient;
  @observable paymentMethod: any; // PaymentMethodModel;
  @observable visitTag: VisitTagModel | undefined; // VisitTagModel;
  @observable reservation: ReservationModel | undefined;
  @observable arrived: DateTime | undefined;
  @observable firstCheckIn: DateTime | undefined;
  @observable visitStarted: DateTime | undefined;
  @observable arrivedWaitingTime: number | undefined;
  @observable checkInWaitingTime: number | undefined;
  @observable intakeId: number | undefined;
  @observable intakePetId: number | undefined;
  @observable intakePublicId: string | undefined;
  @observable intakeCompletionStatusId: IntakeCompletionStatuses | undefined;
  @observable intakeCompletionStatus: IVisitIntakeCompletionStatus;

  constructor(params: Partial<IVisitListing>) {
    super(params);
    this.clinicId = _.defaultTo(params.clinicId, 0);
    this.visitId = _.defaultTo(params.visitId, 0);
    this.petId = _.defaultTo(params.petId, 0);
    this.clientId = _.defaultTo(params.clientId, 0);
    this.isExpress = _.defaultTo(params.isExpress, false);
    this.isRxOnly = _.defaultTo(params.isRxOnly, false);
    this.visitStatusId = _.defaultTo(params.visitStatusId, 0 as VisitStatus);
    this.visitStatusName = _.defaultTo(params.visitStatusName, '');
    this.visitCompletionTypeId = _.defaultTo(params.visitCompletionTypeId, 0);
    this.visitCompletionTypeName = _.defaultTo(
      params.visitCompletionTypeName,
      '',
    );
    this.visitCompletionTypeIsComplete = _.defaultTo(
      params.visitCompletionTypeIsComplete,
      false,
    );
    this.visitCompletionTypeIsManuallyAssignable = _.defaultTo(
      params.visitCompletionTypeIsManuallyAssignable,
      false,
    );

    this.checkinTime = DateTime.fromISO(
      _.defaultTo(params.checkinTime, '2000-01-01'),
      { zone: 'utc' },
    );
    this.consultationStart = DateTime.fromISO(
      _.defaultTo(params.consultationStart, '2000-01-01'),
      { zone: 'utc' },
    );
    this.consultation2Start = DateTime.fromISO(
      _.defaultTo(params.consultation2Start, '2000-01-01'),
      { zone: 'utc' },
    );
    this.vetServicesStart = DateTime.fromISO(
      _.defaultTo(params.vetServicesStart, '2000-01-01'),
      { zone: 'utc' },
    );
    this.checkoutTime = DateTime.fromISO(
      _.defaultTo(params.checkoutTime, '2000-01-01'),
      { zone: 'utc' },
    );
    this.visitCheckinStart = DateTime.fromISO(
      _.defaultTo(params.visitCheckinStart, '2000-01-01'),
      { zone: 'utc' },
    );
    this.visitConsultationStart = DateTime.fromISO(
      _.defaultTo(params.visitConsultationStart, '2000-01-01'),
      { zone: 'utc' },
    );
    this.visitVetServicesStart = DateTime.fromISO(
      _.defaultTo(params.visitVetServicesStart, '2000-01-01'),
      { zone: 'utc' },
    );
    this.visitCheckoutStart = DateTime.fromISO(
      _.defaultTo(params.visitCheckoutStart, '2000-01-01'),
      { zone: 'utc' },
    );
    this.visitInProgressStart = DateTime.fromISO(
      _.defaultTo(params.visitInProgressStart, '2000-01-01'),
      { zone: 'utc' },
    );
    this.visitAdmittedStart = DateTime.fromISO(
      _.defaultTo(params.visitAdmittedStart, '2000-01-01'),
      { zone: 'utc' },
    );
    this.visitFinalizedStart = DateTime.fromISO(
      _.defaultTo(params.visitFinalizedStart, '2000-01-01'),
      { zone: 'utc' },
    );
    this.visitPaidStart = DateTime.fromISO(
      _.defaultTo(params.visitPaidStart, '2000-01-01'),
      { zone: 'utc' },
    );

    this.smsStatus = _.defaultTo(params.smsStatus, 0);
    this.hasVetAttestation = _.defaultTo(params.hasVetAttestation, false);
    this.paymentMethodId = _.defaultTo(params.paymentMethodId, undefined);
    this.cashPayment = _.defaultTo(params.cashPayment, undefined);
    this.processedPhotoBaseUrl = _.defaultTo(params.processedPhotoBaseUrl, '');
    this.photoUrl = _.defaultTo(params.photoUrl, '');
    this.serviceAmount = _.defaultTo(params.serviceAmount, 0);
    this.productAmount = _.defaultTo(params.productAmount, 0);
    this.licenseFeeAmount = _.defaultTo(params.licenseFeeAmount, 0);
    this.vetcoPrescriptionAmount = _.defaultTo(
      params.vetcoPrescriptionAmount,
      0,
    );
    this.discountAmount = _.defaultTo(params.discountAmount, 0);
    this.taxPercentage = _.defaultTo(params.taxPercentage, 0);
    this.subtotalPreDiscountsAmount = _.defaultTo(
      params.subtotalPreDiscountsAmount,
      0,
    );
    this.subtotalPostDiscountsAmount = _.defaultTo(
      params.subtotalPostDiscountsAmount,
      0,
    );
    this.taxAmount = _.defaultTo(params.taxAmount, 0);
    this.totalAmount = _.defaultTo(params.totalAmount, 0);
    this.servicesRebateAmount = _.defaultTo(
      params.servicesRebateAmount,
      undefined,
    );
    this.productsRebateAmount = _.defaultTo(
      params.productsRebateAmount,
      undefined,
    );
    this.totalRebateAmount = _.defaultTo(params.totalRebateAmount, undefined);
    this.nonTaxableFeeAmount = _.defaultTo(
      params.nonTaxableFeeAmount,
      undefined,
    );
    this.taxableFeeAmount = _.defaultTo(params.taxableFeeAmount, undefined);
    this.totalFeeAmount = _.defaultTo(params.totalFeeAmount, undefined);
    this.totalAOVAmount = _.defaultTo(params.totalAOVAmount, undefined);
    this.visitTagId = _.defaultTo(params.visitTagId, undefined);
    this.reservationId = _.defaultTo(params.reservationId, undefined);
    this.approvedForVaccinations = _.defaultTo(
      params.approvedForVaccinations,
      undefined,
    );

    this.pet = params.pet
      ? new VisitPetModel(params.pet)
      : new VisitPetModel({});
    this.client = params.client
      ? new VisitClientModel(params.client)
      : new VisitClientModel({});
    this.reservation = params.reservation
      ? new ReservationModel(params.reservation as any)
      : undefined;

    this.paymentMethod = _.defaultTo(params.paymentMethod, undefined);
    this.visitTag = params.visitTag
      ? new VisitTagModel(params.visitTag)
      : undefined;

    this.arrived = params.arrived
      ? DateTime.fromISO(params.arrived, { zone: 'utc' })
      : undefined;
    this.firstCheckIn = params.firstCheckIn
      ? DateTime.fromISO(params.firstCheckIn, { zone: 'utc' })
      : undefined;
    this.visitStarted = params.visitStarted
      ? DateTime.fromISO(params.visitStarted, { zone: 'utc' })
      : undefined;

    this.arrivedWaitingTime = _.defaultTo(params.arrivedWaitingTime, undefined);
    this.checkInWaitingTime = _.defaultTo(params.checkInWaitingTime, undefined);
    this.intakeId = _.defaultTo(params.intakeId, undefined);
    this.intakePetId = _.defaultTo(params.intakePetId, undefined);
    this.intakePublicId = _.defaultTo(params.intakePublicId, undefined);
    this.intakeCompletionStatusId = _.defaultTo(
      params.intakeCompletionStatusId,
      undefined,
    );

    this.intakeCompletionStatus = params.intakeCompletionStatus
      ? new VisitIntakeCompletionStatusModel(params.intakeCompletionStatus)
      : new VisitIntakeCompletionStatusModel({});

    makeObservable(this);
  }

  @action.bound
  isFollowUpVisit(): boolean {
    return (
      !_.isNil(this.reservation) &&
      this.reservation.source ===
        SourceSystem[SourceSystem.PetPass].toLowerCase()
    );
  }

  public static mapToVisitListing(
    visit: VisitModel,
  ): VisitListingModel {
    const v = new VisitListingModel({ ...visit, ...visit.pet, ...visit.client } as unknown as IVisitListing); 
    v.clientId = visit.client.clientId;
    v.visitStatusName = visit.visitStatus.name;
    v.pet.speciesName = visit.pet.species?.name!;
    v.visitCompletionTypeName = visit.visitCompletionType.name;
    v.visitCompletionTypeIsComplete = visit.visitCompletionType.isComplete;
    v.visitCompletionTypeIsManuallyAssignable = visit.visitCompletionType.isManuallyAssignable;
    v.visitCheckinStart = _.defaultTo(visit.checkinStart, DateTime.fromISO('2000-01-01', { zone: 'utc' }));
    v.visitConsultationStart = _.defaultTo(visit.consultationStart, DateTime.fromISO('2000-01-01', { zone: 'utc' }));
    v.visitCheckoutStart = _.defaultTo(visit.checkoutStart, DateTime.fromISO('2000-01-01', { zone: 'utc' }));

    if (visit.visitEvent) {
      v.checkinTime = _.defaultTo(visit.visitEvent.checkinTime, DateTime.fromISO('2000-01-01', { zone: 'utc' }));
      v.consultation2Start = _.defaultTo(visit.visitEvent.consultation2Start, DateTime.fromISO('2000-01-01', { zone: 'utc' }));
      v.checkoutTime = _.defaultTo(visit.visitEvent.checkoutTime, DateTime.fromISO('2000-01-01', { zone: 'utc' }));
      v.visitVetServicesStart = _.defaultTo(visit.visitEvent.vetServicesStart, DateTime.fromISO('2000-01-01', { zone: 'utc' }));
      v.visitInProgressStart = _.defaultTo(visit.visitEvent.inProgressStart, DateTime.fromISO('2000-01-01', { zone: 'utc' }));
      v.visitAdmittedStart = _.defaultTo(visit.visitEvent.admittedStart, DateTime.fromISO('2000-01-01', { zone: 'utc' }));
      v.visitFinalizedStart = _.defaultTo(visit.visitEvent.finalizedStart, DateTime.fromISO('2000-01-01', { zone: 'utc' }));
      v.visitPaidStart = _.defaultTo(visit.visitEvent.paidStart, DateTime.fromISO('2000-01-01', { zone: 'utc' }));
    }
    
    v.smsStatus = visit.smsStatus ?? 0;
    v.processedPhotoBaseUrl = visit.pet.photo?.processedPhotoBaseUrl!;
    v.photoUrl = visit.pet.photo?.photoUrl!;
    v.pet.photoUrl = visit.pet.photo?.photoUrl!;
    v.pet.processedPhotoBaseUrl = visit.pet.photo?.processedPhotoBaseUrl ?? '';
     if (!v.pet.azureImage) {
        v.pet.azureImage = { container: visit.pet.photo?.container ?? '', fileName: visit.pet.photo?.fileName ?? '' };
    }
    v.pet.azureImage.container = visit.pet.photo?.container ?? '';
    v.pet.azureImage.fileName = visit.pet.photo?.fileName ?? '';
    v.approvedForVaccinations = visit.visitWellnessExam?.approvedForVaccinations!;
    v.intakePetId = visit.intake.pets?.find(p => p.petId == visit.petId)?.intakePetId;
    v.intakePublicId = visit.intake.intakePublicId;
    return v;
  }

}
